import { useCallback, useEffect, useRef, useState } from 'react';
import { apiJson } from './api';
import { LIST_STATES } from './invoiceStatus';

/** Polls GET /api/documents/invoices?state=… for every state; keeps the last good list on errors. */
export default function useInvoiceLists(intervalMs = 4000) {
  const [byState, setByState] = useState({});
  const [errors, setErrors] = useState({});
  const generation = useRef(0);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    const run = ++generation.current;
    const results = await Promise.all(LIST_STATES.map((s) =>
      apiJson(`/api/documents/invoices?state=${s}`).then((d) => d?.items || []).catch(() => null)));
    if (!alive.current || run !== generation.current) return;
    setByState((prev) => {
      const next = { ...prev };
      LIST_STATES.forEach((s, i) => { if (results[i]) next[s] = results[i]; });
      return next;
    });
    setErrors(Object.fromEntries(LIST_STATES.filter((s, i) => results[i] === null).map((s) => [s, 'Could not refresh'])));
    setLoaded(true);
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    alive.current = true;
    refresh();
    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refresh();
    }, intervalMs);
    return () => { alive.current = false; generation.current += 1; clearInterval(timer); };
  }, [refresh, intervalMs]);

  const all = LIST_STATES.flatMap((s) => byState[s] || []);
  const byDoc = {};
  for (const inv of all) if (inv.document_id) byDoc[inv.document_id] = inv;
  return { byState, all, byDoc, loaded, errors, tick, refresh };
}
